import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutInfo } from '@/mocks/articles';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [contactMsg, setContactMsg] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setContactStatus('success');
      setContactMsg('消息已发送，我们会尽快回复！');
      return;
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d9q8ikfnahb7382ov7og', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.get('name') as string,
          email: formData.get('email') as string,
          message: formData.get('message') as string,
        }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        setContactMsg('消息已发送！感谢您的来信，我们会尽快回复。');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '发送失败，请稍后再试';
        setContactStatus('error');
        setContactMsg(serverMsg);
      }
    } catch {
      setContactStatus('error');
      setContactMsg('网络错误，请稍后重试');
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://chiguawang.com/#organization',
        name: '吃瓜网',
        url: 'https://chiguawang.com',
        description: '最新娱乐八卦吃瓜资讯平台',
        foundingDate: '2020-03',
        email: aboutInfo.contact.email,
        telephone: aboutInfo.contact.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: aboutInfo.contact.address,
          addressLocality: '北京',
          addressRegion: '朝阳区',
          addressCountry: 'CN',
        },
        sameAs: ['https://weibo.com/chiguawang'],
      },
      {
        '@type': 'WebPage',
        '@id': 'https://chiguawang.com/about/#webpage',
        url: 'https://chiguawang.com/about',
        name: '关于我们 - 吃瓜网',
        description: '了解吃瓜网的品牌故事、团队使命、联系方式及常见问题。',
        isPartOf: { '@id': 'https://chiguawang.com/#website' },
        about: { '@id': 'https://chiguawang.com/#organization' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://chiguawang.com' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://chiguawang.com/about' },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: aboutInfo.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Breadcrumb */}
        <div className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 py-3">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-900 font-medium">关于我们</span>
              </nav>
            </div>
          </div>
        </div>

        {/* Hero */}
        <section className="w-full px-4 md:px-6 py-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center bg-primary-100 rounded-2xl mb-5">
              <i className="ri-chat-smile-2-line text-primary-600 text-3xl"></i>
            </div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于吃瓜网
            </h1>
            <p className="text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.slogan} — 创立于{aboutInfo.founded}，国内领先的娱乐八卦资讯平台
            </p>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-5">
                  品牌故事
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {aboutInfo.intro}
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>
              <div className="relative h-72 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20modern%20creative%20office%20space%20with%20young%20team%20brainstorming%20around%20a%20large%20screen%2C%20colorful%20sticky%20notes%20on%20glass%20walls%2C%20warm%20coral%20and%20terracotta%20accents%2C%20collaborative%20and%20energetic%20atmosphere%2C%20editorial%20office%20photography%2C%20bright%20natural%20light&width=800&height=600&seq=about-brand-story&orientation=landscape"
                  alt="吃瓜网团队"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="w-full px-4 md:px-6 py-14">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2 text-center">
              核心价值观
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              引领我们前行的四大理念
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {aboutInfo.values.map((val, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-2xl p-6 border border-background-200/60"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-xl mb-4">
                    <span className="text-accent-600 font-heading font-bold text-lg">{i + 1}</span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{val.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{val.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2 text-center">
              数据口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              数字见证我们的成长与用户认可
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.reputation.map((rep, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-2xl p-6 text-center border border-background-200/60"
                >
                  <div className="text-3xl font-heading font-bold text-primary-500 mb-2">
                    {rep.metric}
                  </div>
                  <p className="text-sm text-foreground-500">{rep.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact + Form */}
        <section className="w-full px-4 md:px-6 py-14">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              有任何问题或建议？欢迎随时联系我们
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="flex flex-col gap-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-xl shrink-0">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h4>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-xl shrink-0">
                    <i className="ri-weibo-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">微博</h4>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.weibo}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-xl shrink-0">
                    <i className="ri-wechat-line text-secondary-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">微信</h4>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-background-200 rounded-xl shrink-0">
                    <i className="ri-map-pin-line text-foreground-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">地址</h4>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-background-200 rounded-xl shrink-0">
                    <i className="ri-phone-line text-foreground-600 text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">电话</h4>
                    <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
              </div>

              <div>
                <form
                  onSubmit={handleContactSubmit}
                  data-readdy-form
                  className="bg-background-100 rounded-2xl p-6 border border-background-200/60"
                >
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                    发送消息
                  </h3>
                  <input
                    type="text"
                    name="company_alt"
                    className="antispam-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <div className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        placeholder="请输入你的姓名"
                        className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        placeholder="请输入你的邮箱"
                        className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        消息
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        required
                        maxLength={500}
                        rows={4}
                        placeholder="请输入你想说的话（最多500字）"
                        className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      />
                      <p className="text-xs text-foreground-400 mt-1 text-right">{contactForm.message.length}/500</p>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </div>
                  {contactStatus !== 'idle' && (
                    <p className={`mt-4 text-sm text-center ${contactStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                      {contactMsg}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2 text-center">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">
              快速解答你的疑问
            </p>
            <div className="flex flex-col gap-4">
              {aboutInfo.faqs.map((faq, i) => (
                <details
                  key={i}
                  className="group bg-background-50 rounded-2xl border border-background-200/60"
                >
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <h3 className="text-sm font-semibold text-foreground-900 pr-4">{faq.question}</h3>
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-background-100 group-open:bg-primary-100 shrink-0 transition-colors">
                      <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform text-sm"></i>
                    </div>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}